import React, { useRef, ReactNode, useCallback } from 'react';
import { useSwipeGestures, SwipeGestureConfig } from '../../hooks/useSwipeGestures';
import { useSwipePreferences, SwipeActionType } from '../../contexts/SwipePreferencesContext';
import { Post, User } from '../../types';
import { useNotifications } from '../../hooks/useNotifications';

interface SwipeablePostCardProps {
  children: ReactNode;
  post: Post | any; // Support pour CommunityPost aussi
  user: User | null;
  onLike?: () => void;
  onReply?: () => void;
  onShare?: () => void;
  onBookmark?: () => void;
  onReport?: () => void;
  onMute?: () => void;
  onHide?: () => void;
  className?: string;
}

const SwipeablePostCard: React.FC<SwipeablePostCardProps> = ({
  children,
  post,
  user,
  onLike,
  onReply,
  onShare,
  onBookmark,
  onReport,
  onMute,
  onHide,
  className = ''
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { preferences, getSwipeAction } = useSwipePreferences();
  const { addNotification } = useNotifications();

  // Créer les actions de swipe
  const createSwipeAction = useCallback((actionType: SwipeActionType) => {
    // Mapping des actions
    const actionCallbacks = {
      like: onLike,
      reply: onReply,
      share: onShare,
      bookmark: onBookmark,
      report: onReport,
      mute: onMute,
      hide: onHide
    };

    const callback = actionCallbacks[actionType];
    if (!callback) {
      // Notification pour les actions non implémentées
      return () => {
        addNotification({
          message: `Fonctionnalité "${actionType}" bientôt disponible !`,
          type: 'info'
        });
      };
    }
    return callback;
  }, [onLike, onReply, onShare, onBookmark, onReport, onMute, onHide, addNotification]);

  // Configuration des gestes
  const swipeConfig: SwipeGestureConfig = {
    leftAction: preferences.isEnabled ? getSwipeAction(
      preferences.leftAction, 
      createSwipeAction(preferences.leftAction)
    ) : undefined,
    rightAction: preferences.isEnabled ? getSwipeAction(
      preferences.rightAction, 
      createSwipeAction(preferences.rightAction)
    ) : undefined,
    threshold: preferences.threshold,
    maxThreshold: preferences.maxThreshold
  };

  // Appliquer les gestes de swipe
  useSwipeGestures(swipeConfig, cardRef);

  return (
    <div
      ref={cardRef}
      className={`relative swipeable-post-card ${className}`}
      style={{
        // Assurer que l'élément peut être transformé
        willChange: 'transform',
        // Empêcher la sélection de texte pendant le swipe
        userSelect: 'none',
        WebkitUserSelect: 'none',
        // Cursor pour indiquer l'interactivité sur desktop
        cursor: preferences.isEnabled ? 'grab' : 'default'
      }}
      onDragStart={(e) => e.preventDefault()} // Empêcher le drag natif
    >
      {children}
      
      {/* Style inline pour le cursor pendant le drag */}
      {preferences.isEnabled && (
        <style>
          {`.swipeable-post-card:active { cursor: grabbing !important; }`}
        </style>
      )}
    </div>
  );
};

export default SwipeablePostCard;
